import { config, featureEnabled } from '@grafana/runtime';
import { FlagKeys, getFeatureFlagClient } from '@grafana/runtime/internal';
import { NavID } from 'app/core/navtree/constants';
import { addNavEntries } from 'app/core/navtree/registry';
import { contextSrv } from 'app/core/services/context_srv';
import { AccessControlAction } from 'app/types/accessControl';

import { AccessControlAction as EnterpriseAccessControlAction } from '../types';

const has = (action: string) => contextSrv.hasPermission(action);
const hasAny = (...actions: string[]) => actions.some(has);

/**
 * Registers the enterprise nav items with the client-built nav tree
 * (grafana.multiTenantNavTree), replacing the server-side index-data hooks in
 * src/pkg/extensions for that path. The hooks stay in place for the
 * server-built tree; when the flag is on they no-op (empty root) and these
 * entries take over. Gates mirror each hook's conditions with their
 * client-evaluable equivalents.
 *
 * Text is left untranslated here: nav titles and subtitles are translated by
 * item id in grafana's navBarItem-translations, whose lookup takes precedence
 * over whatever an entry sets. These strings are only the English fallback,
 * matching how the core sections declare theirs.
 */
export function registerEnterpriseNavEntries() {
  addNavEntries(
    {
      // pkg/licensing/api/api.go — osslicensing.PageAccess
      parentId: NavID.cfgGeneral,
      entry: {
        when: () => hasAny(EnterpriseAccessControlAction.LicensingRead, AccessControlAction.ActionServerStatsRead),
        build: () => ({
          text: 'Statistics and licensing',
          id: 'licensing',
          url: '/admin/licensing',
          icon: 'unlock',
          sortWeight: -1,
        }),
      },
    },
    {
      // src/pkg/extensions/bannersettings/service.go
      parentId: NavID.cfgGeneral,
      entry: {
        when: () =>
          Boolean(featureEnabled('announcementBanner')) && has(EnterpriseAccessControlAction.ActionBannersWrite),
        build: () => ({
          text: 'Announcement banner',
          subTitle: 'Show important updates and information at the top of every page',
          id: 'banner-settings',
          url: '/admin/banner-settings',
          icon: 'bell',
          sortWeight: 7,
        }),
      },
    },
    {
      // Cloud-only SMTP settings page; the Go nav hook for the server-built tree uses the same gate.
      // The flag keeps this link in sync with the Go nav link, since the frontend can run against an older backend.
      // Bootdata only knows the action, not its scope: the backend enforces settings:smtp:* and the page
      // shows its permission alert on a 403/404.
      parentId: NavID.cfgGeneral,
      entry: {
        when: () =>
          getFeatureFlagClient().getBooleanValue(FlagKeys.GrafanaSmtpSettingsPage, false) &&
          Boolean(featureEnabled('grafanacloud')) &&
          has(AccessControlAction.SettingsWrite),
        build: () => ({
          text: 'SMTP settings',
          subTitle: 'Configure the SMTP server used to send emails',
          id: 'smtp-settings',
          url: '/admin/settings/smtp',
          icon: 'envelope',
          sortWeight: 1,
        }),
      },
    },
    {
      // src/pkg/extensions/recordedqueries/service.go — the hook requires the
      // org role to be exactly Editor or Admin (not hierarchical)
      parentId: NavID.cfgPlugins,
      entry: {
        when: () =>
          Boolean(featureEnabled('recordedqueries')) &&
          Boolean(config.recordedQueries?.enabled) &&
          (contextSrv.hasRole('Editor') || contextSrv.hasRole('Admin')),
        build: () => ({
          text: 'Recorded queries',
          id: 'recordedQueries',
          url: '/recorded-queries',
          icon: 'record-audio',
          sortWeight: 1,
        }),
      },
    },
    {
      // src/pkg/registry/secret/ui_navtree_provider.go
      parentId: NavID.cfg,
      entry: {
        when: () =>
          getFeatureFlagClient().getBooleanValue(FlagKeys.SecretsManagementAppPlatformUI, false) &&
          Boolean(featureEnabled('secretsManager')) &&
          hasAny(
            EnterpriseAccessControlAction.SecretSecureValuesCreate,
            EnterpriseAccessControlAction.SecretSecureValuesRead,
            EnterpriseAccessControlAction.SecretKeepersCreate,
            EnterpriseAccessControlAction.SecretKeepersRead
          ),
        build: () => ({
          text: 'Secrets Management',
          subTitle: 'Manage secrets for your Grafana instance',
          id: 'secrets-management',
          url: '/admin/secrets',
          icon: 'lock',
          sortWeight: 7,
        }),
      },
    },
    {
      // src/pkg/extensions/query-library/ui_navtree_provider.go. The Go hook
      // anchor-inserts after Variables/Library panels; here it places by sort
      // order like every other registered item.
      parentId: NavID.dashboards,
      entry: {
        when: () =>
          getFeatureFlagClient().getBooleanValue(FlagKeys.GrafanaSavedQueriesPage, false) &&
          has(EnterpriseAccessControlAction.QueriesRead),
        build: () => ({
          text: 'Saved queries',
          subTitle: 'Reusable queries across Grafana',
          id: 'saved-queries',
          url: '/saved-queries',
          icon: 'book-open',
        }),
      },
    },
    {
      // src/pkg/extensions/report/report.go. The hook that adds this item is
      // registered from init(), which the service skips when reporting is
      // disabled, and the routes are gated the same way.
      parentId: NavID.dashboards,
      entry: {
        when: () =>
          Boolean(config.reporting?.enabled) &&
          Boolean(featureEnabled('reports')) &&
          has(EnterpriseAccessControlAction.ReportingRead),
        build: () => ({
          text: 'Reporting',
          subTitle: 'Create and manage PDF reports distributed via e-mail',
          id: 'reports',
          url: '/reports',
          icon: 'file-alt',
        }),
      },
    }
  );
}
